
import {
  ApiService,
  BarrelItemOutput,
  PaymentOutput,
  OutstandingPayment,
  SimpleBarrelStatusOutput, BarrelUpgrade,
} from "@/services/ApiService";
import { error } from "@/services/LoggerService";
import BarrelBreakdown from "@/components/BarrelBreakdown.vue";
import BarrelStatusHistoryRecord from "@/components/BarrelStatusHistoryRecord.vue";
import BarrelPaymentRecord from "@/components/BarrelPaymentRecord.vue";
import OutstandingPaymentRecord from "@/components/OutstandingPaymentRecord.vue";
import AddItem from "@/components/AddItem.vue";
import ConsigneeInfo from "@/components/ConsigneeInfo.vue";
import Modal from "@/components/Modal.vue";
import mixins from "vue-typed-mixins";
import FullnessDropdown from "@/components/FullnessDropdown.vue";
import ItemTable from "@/components/ItemTable.vue";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import StatusDropdown from "@/components/StatusDropdown.vue";
import ViewDocuments from "@/components/ViewDocuments.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import StackingNotification from "@/components/StackingNotification.vue";
import StackingNotificationMixins from "@/mixins/StackingNotificationMixins";
import BarrelStatusMixin, { Status } from "@/mixins/BarrelStatusMixin";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import ConfirmationModalMixin from "@/mixins/ConfirmationModalMixins";
import UpgradeDropdown from "@/components/UpgradeDropdown.vue"
import BarrelUpgradeRecord from "@/components/BarrelUpgradeRecord.vue";

type TableDisplay = "history" | "items" | "payments" | "upgrades";

const NOT_ALL_PACKED_MESSAGE = 'Not all permitted items are packed';

export default mixins(SnackbarNotifierMixin,StackingNotificationMixins, BarrelStatusMixin, ConfirmationModalMixin).extend({
  name: "ManageBarrel",
  data() {
    return {
      barrelId: "" as string,
      cancelButtonChecks: [ 'Delivery Complete', 'Pickup Complete', 'Cancelled'],
      contents: [] as BarrelItemOutput[],
      statusHistoryRecords: [] as SimpleBarrelStatusOutput[],
      payments: [] as PaymentOutput[],
      outstandingPayments: [] as OutstandingPayment[],
      upgrades: [] as BarrelUpgrade[],
      tableDisplay: "items" as TableDisplay,
      showModal: false,
      showViewDocModal: false,
      barrel: {} as any,
      fetching: false
    };
  },
  components: {
    BarrelBreakdown,
    BarrelStatusHistoryRecord,
    BarrelPaymentRecord,
    OutstandingPaymentRecord,
    AddItem,
    ItemTable,
    FullnessDropdown,
    Modal,
    SnackbarNotifier,
    StatusDropdown,
    ConsigneeInfo,
    ViewDocuments,
    StackingNotification,
    ConfirmationModal,
    UpgradeDropdown,
    BarrelUpgradeRecord
  },
  computed: {
    sealed():boolean{
      return this.isBarrelSealed(this.barrel.BarrelStatus);
    },
    areAllItemsPacked(): boolean{
      return this.contents.every(entry => entry.Items.every(item => {
        if(!item.Permitted) return true;
        return item.Packed
      }));
    },
    barrelCollected(): boolean{
      return this.barrel.BarrelStatus === Status.PickupComplete || this.barrel.BarrelStatus === Status.DeliveryComplete;
    },
    hasOutstandingPayments(): boolean{
      if(!this.outstandingPayments){
        return false;
      }

      return this.outstandingPayments.length > 0;
    },
    hasUpgrades(): boolean{
      if(!this.upgrades){
        return false;
      }

      return this.upgrades.length > 0;
    },
    historyTab() {
      return "history";
    },
    itemsTab() {
      return "items";
    },
    paymentsTab() {
      return "payments";
    },
    outstandingPaymentsTab() {
      return "outstandingPayments";
    },
    upgradesTab(){
      return "upgrades"
    }
  },
  methods: {
    unpackedItemsNotification(){
      this.addNotification({
        message: NOT_ALL_PACKED_MESSAGE
      })
      window.location.href = '#itemsTable';
    },
    async cancelBarrel(){
      try{
        const shouldCancel = await this.confirm();
        if(!shouldCancel) return;
        this.startConfirmationLoading();
        let result = await ApiService.Instance.cancelBarrel(this.barrelId);
        this.stopConfirmationLoading();
        this.showNotifier({
        message: 'Barrel Cancelled Successfully',
        snackbarType: 'success'
      });
      if (result) setTimeout(async () => await this.getDetails(), 1000);
      }
      catch(e) {
        this.stopConfirmationLoading();
        this.showNotifier({
        message: 'Something went wrong cancelling barrel.',
        snackbarType: 'error'
      });
        error(e);
      }
    },
    setStatusFailedNotification(message: string){
      if(message.includes("outstanding document(s)")){
        this.$nextTick(() =>{
          this.showViewDocModal = true;
        });
      }

      this.addNotification({message});
    },
    async updateDetails(delayValue: number) {
      const vm = this;
      const detailsFunction = this.getDetails;
      this.showNotifier({
        message: 'Successfuly Updated Barrel',
        snackbarType: 'success'
      });

      if(!delayValue || Number(delayValue) !== delayValue){
        delayValue = 1000;
      }

      setTimeout(async function () {
        await detailsFunction();
        vm.$emit('updateNotification')
      }, delayValue);
    },
    async sealBarrel() {
      try {
        let result = await ApiService.Instance.setBarrelStatus({
          status: "seal",
          barrelId: this.barrelId,
        });
        if (result) setTimeout(async () => await this.getDetails(), 1000);
      } catch (e) {
        error(e);
      }
    },
    formatAddress(address: any) {
      let addrString = `${address.Line1}, `;
      if (address.Line2) addrString += `${address.Line2}, `;
      addrString += `\n${address.Town}, ${address.Parish},\n ${address.Country}`;
      return addrString;
    },
    async getDetails() {
      try {
        if (!this.barrelId) return;
        let temp = this.barrelId;
        this.barrelId = "";
        this.fetching = true;
        let result = await ApiService.Instance.barrelDetails({
          logicalBarrelId: temp,
        });
        this.fetching = false;
        this.barrelId = temp;
        this.barrel = result.barrel;
        this.contents = result.content;
        this.statusHistoryRecords = result.statusHistory;
        this.payments = result.payments;
        this.outstandingPayments = result.outstandingPayments;
        this.upgrades = result.upgrades;
      } catch (e) {
        this.fetching = false;
        this.showNotifier({
          message: `Failed To Load Barrel Info`,
          snackbarType: "error",
        });
        error(e);
      }
    },
  },
  async beforeMount() {
    try {
      this.barrelId = this.$route.params.barrelId as string;
      if (this.barrelId) await this.getDetails();
      if(this.barrel.BarrelName)this.showNotifier({
        message: `Successfully Load Barrel Info`,
        snackbarType: "success",
      });
    } catch (e) {
      error(e);
    }
  },
});
